import React from 'react';
import InlineLoading from './InlineLoading';

const resolveId = (item) =>
  item?.id ?? item?.value ?? item?.categoryId ?? item?.name ?? item;
const resolveLabel = (item) =>
  item?.label ?? item?.name ?? String(resolveId(item) ?? '');
const resolveInitial = (item) => {
  const label = resolveLabel(item);
  const trimmed = label.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
};

const CategoryPicker = ({
  title = 'Categorias',
  isOpen = true,
  onToggle,
  categories = [],
  selectedCategory,
  onSelect,
  includeAll = false,
  allItem = { id: 'all', label: 'Todos', initial: 'T' },
  leadingItems = [],
  layout = 'scroll',
  compact = false,
  loading = false,
  loadingMessage = <InlineLoading label="Cargando categorias" />,
  error,
  emptyMessage,
  isEmpty,
  toggleClassName = 'p-4 bg-white text-black rounded-lg shadow mb-4 cursor-pointer flex items-center justify-between',
  itemsWrapperClassName = '',
  gridClassName = 'grid grid-cols-1 md:grid-cols-2 gap-3',
  scrollClassName = 'flex items-center gap-3 overflow-x-auto pb-2',
}) => {
  const shouldShow = isOpen !== false;
  const allCategory = includeAll ? [allItem] : [];
  const items = [...allCategory, ...leadingItems, ...categories];
  const normalizedSelected = selectedCategory == null ? '' : String(selectedCategory);
  const showEmpty = Boolean(
    emptyMessage && !loading && !error && (isEmpty ?? categories.length === 0)
  );
  const resolvedScrollClassName = compact
    ? 'flex items-center gap-1.5 overflow-x-auto pb-1'
    : scrollClassName;

  const handleSelect = (item) => {
    if (!onSelect) return;
    onSelect(resolveId(item));
  };

  const renderStaticItem = (item) => {
    const id = resolveId(item);
    const label = resolveLabel(item);
    const image = item?.image;
    const isSelected = String(id) === normalizedSelected;
    return (
      <button
        key={String(id)}
        type="button"
        className={`flex items-center gap-4 p-3 bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer ${
          isSelected ? 'border-2 border-blue-500' : ''
        }`}
        onClick={() => handleSelect(item)}
      >
        {image && (
          <img src={image} alt={label} className="w-16 h-16 object-cover rounded" />
        )}
        <p className="text-lg font-semibold">{label}</p>
      </button>
    );
  };

  const renderDynamicItem = (item) => {
    const id = resolveId(item);
    const label = resolveLabel(item);
    const initial = item?.initial ?? resolveInitial(item);
    const isSelected = String(id) === normalizedSelected;
    return (
      <button
        key={String(id)}
        type="button"
        onClick={() => handleSelect(item)}
        className={`shrink-0 border bg-white shadow-sm hover:shadow transition-shadow ${
          compact ? 'w-16 h-16 rounded-lg' : 'w-24 h-24 rounded-lg'
        } ${
          isSelected ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200'
        }`}
        aria-pressed={isSelected}
        title={label}
      >
        <div className="flex flex-col items-center justify-center h-full px-2">
          <div className={`${compact ? 'text-sm' : 'text-lg'} font-bold text-gray-700`}>
            {initial}
          </div>
          <div className={`${compact ? 'text-[10px] mt-0.5' : 'text-[11px] mt-1'} font-semibold text-gray-700 text-center leading-tight`}>
            {label}
          </div>
        </div>
      </button>
    );
  };

  return (
    <>
      {onToggle && (
        <div className={toggleClassName} onClick={onToggle}>
          <span className="text-lg font-bold">{title}</span>
          <span className="text-xl">{shouldShow ? '▲' : '▼'}</span>
        </div>
      )}
      {shouldShow && (
        <div className={itemsWrapperClassName}>
          <div className={layout === 'grid' ? gridClassName : resolvedScrollClassName}>
            {items.map(layout === 'grid' ? renderStaticItem : renderDynamicItem)}
          </div>
          {loading && (
            <div className="text-sm text-gray-500 mt-2">{loadingMessage}</div>
          )}
          {error && !loading && (
            <div className="text-sm text-red-600 mt-2">{error}</div>
          )}
          {showEmpty && (
            <div className="text-sm text-gray-600 mt-2">{emptyMessage}</div>
          )}
        </div>
      )}
    </>
  );
};

export default CategoryPicker;
